import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  evenementsApi,
  useActionEvenement,
  useEvenement,
  useInscrits,
  type ResultatEmargement,
} from '../../api/evenements.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Scanner, type DecoderQr } from '../inventaire/Scanner.js';
import { InscritsEvenement } from './InscritsEvenement.js';
import styles from './Evenements.module.css';

export function AccueilEvenementPage({ decoderQr }: { decoderQr?: DecoderQr }) {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const autorise = galerieALaction(galerie, 'events:checkin');
  const fiche = useEvenement(galerieSlug, id, autorise);
  const inscrits = useInscrits(galerieSlug, id, {}, autorise);
  const [resultat, setResultat] = useState<ResultatEmargement | null>(null);
  const scan = useActionEvenement(galerieSlug, (code: string, appel) =>
    evenementsApi.emarger(galerieSlug, id, code, appel),
  );
  if (!autorise) return <p role="alert">{t('evenements.accesRefuse')}</p>;
  const evenement = fiche.data?.evenement;
  const presents =
    inscrits.data?.inscriptions
      .filter((i) => i.presentLe && i.etat === 'confirmee')
      .reduce((n, i) => n + i.places, 0) ?? 0;
  const couleur = resultat?.resultat === 'admis' ? styles.admis : styles.refus;
  const ton = resultat?.resultat === 'deja_emarge' ? styles.deja_emarge : couleur;
  return (
    <section className={`${styles.page} ${styles.accueil}`}>
      <header>
        <Link to={`/${galerieSlug}/evenements/${id}`}>{t('evenements.retourFiche')}</Link>
        <h1>
          {t('evenements.accueil')} ·{' '}
          {evenement?.titre[i18n.language === 'en' ? 'en' : 'fr'] || evenement?.titre.fr}
        </h1>
      </header>
      <p role="status">
        {t('evenements.compteurPresence', { presents, attendus: evenement?.placesConfirmees ?? 0 })}
      </p>
      {fiche.isPending ? <p role="status">{t('commun:chargement')}</p> : null}
      {fiche.isError || inscrits.isError || scan.isError ? (
        <p role="alert">{t('commun:erreurReseau')}</p>
      ) : null}
      <Scanner
        decoderQr={decoderQr}
        desactive={!evenement}
        onScan={async (code) => {
          setResultat(null);
          const r = await scan.mutateAsync(code);
          setResultat(r);
        }}
      />
      <div aria-live="polite" aria-atomic="true">
        {resultat ? (
          <div className={`${styles.resultat} ${ton}`}>
            <strong>{t(`evenements.resultats.${resultat.resultat}`)}</strong>
            {resultat.resultat === 'admis' && resultat.inscription ? (
              <p>
                {resultat.inscription.nom} ·{' '}
                {t('evenements.nombrePlaces', { count: resultat.inscription.places })}
              </p>
            ) : null}
            {resultat.resultat === 'deja_emarge' && resultat.presentLe ? (
              <p>
                {new Date(resultat.presentLe).toLocaleTimeString(i18n.language, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      {evenement ? (
        <InscritsEvenement evenement={evenement} accueil onResultat={setResultat} />
      ) : null}
    </section>
  );
}
